import { createRoute, redirect, useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useEffect } from "react"

import { accessStore } from "../../access/access-store"
import { isAccessReady } from "../../access/access-state"
import { AccessReadyProvider, useAccessState } from "../../access/use-access"
import { AppShell } from "../../shell/app-shell"
import { AppLoading, ProblemScreen } from "../../shell/states"
import { rootRoute } from "./root-route"

/**
 * Защищённая область кабинета.
 *
 * Маршрут БЕЗ собственного адреса (только id): он не добавляет сегмента в путь, а служит
 * общим родителем всем разделам. Именно здесь кабинет дожидается профиля.
 *
 * ПОЧЕМУ ЖДЁМ В beforeLoad, А НЕ В КОМПОНЕНТЕ. beforeLoad выполняется до отрисовки,
 * и пока он не закончился, маршрутизатор показывает pendingComponent — тот же каркас
 * загрузки, что и при обычной загрузке данных. Компонент, который решал бы о правах
 * сам, обязан был бы что-то нарисовать в первый же кадр, и этим «что-то» оказался бы
 * отказ в доступе человеку, у которого доступ есть.
 *
 * ПРАВА РАЗДЕЛОВ ЗДЕСЬ НЕ ПРОВЕРЯЮТСЯ. Отсутствие права — это не повод перенаправлять:
 * отказ показывается ПО АДРЕСУ РАЗДЕЛА (см. SectionGuard), чтобы работала кнопка «назад»
 * и чтобы ссылку можно было переслать владельцу.
 */
export const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "cabinet",
  beforeLoad: async ({ context, location }) => {
    const state = await context.access.ensureLoaded()
    if (state.kind === "anonymous") {
      throw redirect({ to: "/login", search: { next: location.href } })
    }
    if (state.kind === "tenant_required") {
      throw redirect({ to: "/choose-salon", search: { next: location.href } })
    }
  },
  pendingComponent: AppLoading,
  component: ProtectedLayout,
})

function ProtectedLayout(): ReactNode {
  const state = useAccessState()
  const router = useRouter()

  // Сессия может закончиться посреди работы: например, запрос журнала наткнулся
  // на украденный refresh. beforeLoad к тому моменту давно отработал, поэтому уводит
  // отсюда эффект, а не маршрут.
  useEffect(() => {
    if (state.kind === "anonymous") {
      void router.navigate({ to: "/login", search: { next: router.state.location.href } })
    }
    if (state.kind === "tenant_required") {
      void router.navigate({ to: "/choose-salon", search: {} })
    }
  }, [state.kind, router])

  const retry = useCallback(() => {
    void accessStore.reload()
  }, [])

  if (state.kind === "failed") {
    return <ProblemScreen problem={state.problem} onRetry={retry} />
  }

  // Пока идёт переход на вход или выбор салона — каркас загрузки, а не отказ.
  if (!isAccessReady(state)) return <AppLoading />

  return (
    <AccessReadyProvider access={state}>
      <AppShell />
    </AccessReadyProvider>
  )
}
